// Lib
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

// Utils
import logger from '../../logger';
import getClientConfig from '../../getClientConfig';
import { getIsSwiftHybrid } from '../../../hybridApp/hybridAppUtils';

import {
    hasMobileHybridBridge,
    configureMobileHybridHttp,
    handleMobileHybridHttpRequest,
} from './mobileHybridHttpBridge';

// Interceptors
import requestAuthorisationInterceptor from './requestAuthorisationInterceptor';
import responseRetryInterceptor from '../../../../common/utils/http/responseRetryInterceptor';
import requestNoCacheInterceptor from './requestNoCacheInterceptor';

// Constants
import { METHODS } from '../../../../common/utils/http/httpConstants';

const clientConfig = getClientConfig();
const apiRootUrl = clientConfig.apiRoot || '';

const DEFAULT_CONFIG: AxiosRequestConfig = {
    method: METHODS.GET,
    timeout: 21000,
    responseType: 'json',
    prefix: `${apiRootUrl}/api`,
    headers: {
        'Accept-Version': clientConfig.appVersion || '1.0.0',
        'Content-Type': 'application/json',
    },
};

// Request interceptors
axios.interceptors.request.use(requestAuthorisationInterceptor);
axios.interceptors.request.use(requestNoCacheInterceptor);

if (process.env.APPLICATION_MODE === 'mobile-hybrid') {
    configureMobileHybridHttp();
}

// Response interceptors
const retryErrorCb = (err: AxiosError) =>
    logger.warn(
        `RETRYING: A http error occurred (${(err.config?.retry || 0) - (err.config?._retryCount || 0)} ` +
            'attempts remaining)',
        err,
        err.response,
        JSON.stringify(err),
    );
axios.interceptors.response.use(undefined, responseRetryInterceptor(axios, retryErrorCb));

export const http = <BodyType = any, ResponseType = any>(
    config: AxiosRequestConfig<BodyType>,
): Promise<AxiosResponse<ResponseType, BodyType>> => {
    const axiosConfig = {
        ...DEFAULT_CONFIG,
        ...config,
    };

    axiosConfig.url = `${axiosConfig.prefix}/${config.url}`;

    if (getIsSwiftHybrid() && hasMobileHybridBridge) {
        return handleMobileHybridHttpRequest(axiosConfig);
    }

    return axios(axiosConfig);
};
